/**
 * InnerMirrorCard — client-side 1080×1920 PNG card renderer.
 *
 * Uses a hidden offscreen <canvas>; the visible preview is a scaled DOM
 * replica so the user sees exactly what they'll download. Download /
 * Native Share buttons use the actual canvas blob.
 */
import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Share2, Eye, EyeOff } from "lucide-react";

const INDIGO = "#5D00FF";
const AQUA = "#00C2CB";
const INK = "#1C1C1C";

export async function materializeCard(props) {
  return drawCard(props);
}

async function drawCard({ firstName, badge, foundingRank, motivation,
                           discovered, sankalp, publicCard, avatarDataUrl,
                           avatarPublic }) {
  const W = 1080, H = 1920;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  // Background: soft lavender with radial glow
  const g = ctx.createRadialGradient(W / 2, 720, 100, W / 2, 720, 900);
  g.addColorStop(0, "#F2EEFF");
  g.addColorStop(1, "#FFFFFF");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  // Mirror ring
  ctx.strokeStyle = INDIGO;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(W / 2, 780, 320, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = AQUA;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(W / 2, 780, 300, 0, Math.PI * 2);
  ctx.stroke();
  // Draw avatar DP centered in the mirror ring when provided AND
  // either we're exporting the private card or the user explicitly
  // chose to include it on the public card.
  const showAvatar = !!avatarDataUrl && (!publicCard || avatarPublic !== false);
  if (showAvatar) {
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = avatarDataUrl;
      });
      const DP_R = 260;
      ctx.save();
      ctx.beginPath();
      ctx.arc(W / 2, 780, DP_R, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      // Cover-fit the image inside the circle.
      const s = Math.min(img.naturalWidth, img.naturalHeight);
      const sx = (img.naturalWidth - s) / 2;
      const sy = (img.naturalHeight - s) / 2;
      ctx.drawImage(img, sx, sy, s, s,
                     W / 2 - DP_R, 780 - DP_R, DP_R * 2, DP_R * 2);
      ctx.restore();
      // Decorative aqua halo just inside the mirror ring.
      ctx.strokeStyle = AQUA;
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(W / 2, 780, DP_R + 10, 0, Math.PI * 2);
      ctx.stroke();
    } catch (_) { /* fall through — card still renders without DP */ }
  }
  // KALKI wordmark
  ctx.fillStyle = INDIGO;
  ctx.font = "500 48px 'Inter', sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("KALKI", W / 2, 180);
  ctx.fillStyle = INK;
  ctx.font = "italic 500 64px 'EB Garamond', serif";
  ctx.fillText("THE INNER MIRROR", W / 2, 260);
  // Badge
  ctx.fillStyle = badge === "founding_devotee" ? "#FF2E9A" : "#676773";
  ctx.font = "500 32px 'Inter', sans-serif";
  const badgeText = badge === "founding_devotee"
    ? `FOUNDING DEVOTEE · FIRST 110${foundingRank ? ` · #${foundingRank}` : ""}`
    : "KALKI DEVOTEE";
  ctx.fillText(badgeText, W / 2, 330);
  // Name — scales down for long names so it never overlaps the ring
  // or extends beyond the safe margins. Hard-capped at 24 chars.
  const safeName = (firstName || "").slice(0, 24).toUpperCase();
  let nameSize = 120;
  if (safeName.length > 12) nameSize = 92;
  if (safeName.length > 18) nameSize = 72;
  ctx.fillStyle = INK;
  ctx.font = `500 ${nameSize}px 'EB Garamond', serif`;
  ctx.fillText(safeName, W / 2, 500);
  ctx.font = "italic 400 36px 'EB Garamond', serif";
  ctx.fillText('"My journey towards inner fulfillment"', W / 2, 570);

  // Three insight lines (hidden on public if user chose private-only
  // reflections; placeholder dashes keep the layout stable).
  const insights = publicCard
    ? [
        { k: "I CAME SEEKING",  v: motivation || "—" },
        { k: "I DISCOVERED",    v: discovered || "—" },
        { k: "MY SANKALP",      v: (sankalp || "A quiet intention.").slice(0, 100) },
      ]
    : [
        { k: "I CAME SEEKING",  v: "—" },
        { k: "I DISCOVERED",    v: "—" },
        { k: "MY SANKALP",      v: "—" },
      ];
  let y = 1200;
  for (const i of insights) {
    ctx.fillStyle = INDIGO;
    ctx.font = "500 24px 'Inter', sans-serif";
    ctx.fillText(i.k, W / 2, y);
    ctx.fillStyle = INK;
    ctx.font = "500 40px 'EB Garamond', serif";
    // Wrap long lines
    const words = String(i.v).split(/\s+/);
    const lineMax = 24;
    let line = ""; let yy = y + 54;
    for (const w of words) {
      if ((line + " " + w).trim().length > lineMax) {
        ctx.fillText(line.trim(), W / 2, yy);
        line = w; yy += 48;
      } else {
        line = line + " " + w;
      }
    }
    if (line.trim()) ctx.fillText(line.trim(), W / 2, yy);
    y += 180;
  }

  ctx.fillStyle = "#676773";
  ctx.font = "italic 400 32px 'EB Garamond', serif";
  ctx.fillText("A better world begins with a better intention.", W / 2, 1780);
  ctx.fillStyle = INDIGO;
  ctx.font = "500 24px 'Inter', sans-serif";
  ctx.fillText("KALKI · Vedic Intelligence", W / 2, 1830);
  ctx.fillStyle = "#9999A5";
  ctx.font = "400 22px 'Inter', sans-serif";
  ctx.fillText("www.superlife.living", W / 2, 1870);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}

export default function InnerMirrorCard({ firstName, badge, foundingRank,
                                           motivation, discovered, sankalp,
                                           avatarDataUrl, avatarPublic = true }) {
  const [publicCard, setPublicCard] = useState(true);
  const [busy, setBusy] = useState(false);
  const cacheRef = useRef({ key: "", blob: null });

  const materialize = useCallback(async () => {
    const key = `${firstName}|${badge}|${foundingRank}|${motivation}|${discovered}|${sankalp}|${publicCard}|${avatarDataUrl || ""}|${avatarPublic}`;
    if (cacheRef.current.key === key && cacheRef.current.blob) {
      return cacheRef.current.blob;
    }
    const blob = await drawCard({
      firstName, badge, foundingRank,
      motivation, discovered, sankalp, publicCard,
      avatarDataUrl, avatarPublic,
    });
    cacheRef.current = { key, blob };
    return blob;
  }, [firstName, badge, foundingRank, motivation, discovered, sankalp,
      publicCard, avatarDataUrl, avatarPublic]);

  const download = useCallback(async () => {
    setBusy(true);
    try {
      const blob = await materialize();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kalki-inner-mirror-${firstName.toLowerCase()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Card saved to your device.");
    } catch (e) { toast.error("Could not save the card."); }
    setBusy(false);
  }, [materialize, firstName]);

  const share = useCallback(async () => {
    setBusy(true);
    try {
      const blob = await materialize();
      const file = new File([blob], "kalki-inner-mirror.png",
                              { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "KALKI — The Inner Mirror",
          text: "I just completed my Inner Mirror journey with KALKI.",
        });
      } else {
        await download();
      }
    } catch (_) { /* user dismissed */ }
    setBusy(false);
  }, [materialize, download]);

  const badgeLabel = badge === "founding_devotee"
    ? `FOUNDING DEVOTEE · FIRST 110${foundingRank ? ` · #${foundingRank}` : ""}`
    : "KALKI DEVOTEE";

  return (
    <div className="im-card" data-testid="im-card">
      <div className="im-card-preview" aria-hidden="true">
        <div className="im-card-brand">KALKI</div>
        <div className="im-card-title">THE INNER MIRROR</div>
        <div className="im-card-badge">{badgeLabel}</div>
        <div className="im-card-name">{(firstName || "").toUpperCase()}</div>
        {avatarDataUrl && (!publicCard || avatarPublic !== false) ? (
          <div className="im-card-ring im-card-ring--dp">
            <img src={avatarDataUrl} alt="" />
          </div>
        ) : (
          <div className="im-card-ring" />
        )}
        <div className="im-card-insight"><span>MY JOURNEY</span><strong>{(motivation || "—").slice(0, 48)}</strong></div>
        <div className="im-card-insight"><span>I DISCOVERED</span><strong>{(discovered || "—").slice(0, 48)}</strong></div>
        <div className="im-card-insight"><span>MY SANKALP</span><strong>{(sankalp || "—").slice(0, 80)}</strong></div>
        <div className="im-card-footer">A better world begins with a better intention.</div>
      </div>
      <div className="im-card-controls">
        <button className="im-ghost im-ghost-sm" onClick={() => setPublicCard((p) => !p)}
                data-testid="im-card-privacy">
          {publicCard ? <><Eye size={13} /> Public card</>
                      : <><EyeOff size={13} /> Private card</>}
        </button>
        <button className="im-primary" disabled={busy} onClick={download}
                data-testid="im-card-download">
          <Download size={14} /> Download
        </button>
        <button className="im-primary" disabled={busy} onClick={share}
                data-testid="im-card-share">
          <Share2 size={14} /> Share
        </button>
      </div>
      <style>{`
        .im-card { margin-top: 24px; }
        .im-card-preview { position: relative; background: linear-gradient(180deg,#F2EEFF,#FFFFFF);
                           border-radius: 20px; padding: 28px 20px; text-align: center;
                           border: 1px solid rgba(93,0,255,0.1); overflow: hidden; }
        .im-card-brand { font-size: 10px; letter-spacing: 0.4em; color: #5D00FF; }
        .im-card-title { font-family: 'EB Garamond', serif; font-size: 20px;
                         font-style: italic; margin-top: 4px; color: #1C1C1C; }
        .im-card-badge { font-size: 10px; letter-spacing: 0.2em; color: #FF2E9A;
                         margin-top: 8px; }
        .im-card-name { font-family: 'EB Garamond', serif; font-size: 36px;
                         margin: 16px 0 10px; color: #1C1C1C; overflow-wrap: anywhere; }
        .im-card-ring { width: 160px; height: 160px; border-radius: 50%;
                         border: 2px solid #5D00FF; margin: 10px auto;
                         box-shadow: 0 0 0 6px rgba(0,194,203,0.12); }
        .im-card-ring--dp { overflow: hidden;
                             background: linear-gradient(#FFF, #FFF) padding-box,
                                         linear-gradient(135deg, #5D00FF, #00C2CB) border-box;
                             border: 3px solid transparent; }
        .im-card-ring--dp img { width: 100%; height: 100%;
                                  object-fit: cover; display: block;
                                  border-radius: 50%; }
        .im-card-insight { margin-top: 12px; font-size: 12px; color: #1C1C1C; }
        .im-card-insight span { display: block; font-size: 10px;
                                 letter-spacing: 0.24em; color: #5D00FF; }
        .im-card-insight strong { font-family: 'EB Garamond', serif;
                                   font-weight: 500; font-size: 15px;
                                   overflow-wrap: anywhere; }
        .im-card-footer { margin-top: 18px; font-style: italic;
                          color: #676773; font-size: 11px; }
        .im-card-controls { display: flex; gap: 8px; margin-top: 14px;
                            flex-wrap: wrap; justify-content: center; }
      `}</style>
    </div>
  );
}
